import { useMemo } from 'react';
import type { MentionTarget } from '@/components/desktop-side-panel';
import type { ExecutionMode } from '@/components/worktree-controls';
import { useAgentRuntime } from '@/hooks/use-agent-runtime';
import { resolvePersistedAgent } from '@/lib/desktop-shell/cloud-agents';
import { desktopWorkspaceName } from '@/lib/desktop-shell/personal-workspace';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { isLocalMachine } from '@/lib/local-runtime';
import type { NativeAgent, NativeWorkspaceEnvironment } from '@/lib/native-desktop';

type DesktopShellState = ReturnType<DesktopShellStore['getState']>;

interface DesktopAgentConnectionOptions {
  preview: boolean;
  hydrationReady: boolean;
  chatId: string;
  agents: DesktopShellState['agents'];
  agentId: DesktopShellState['agentId'];
  restoredModelId: DesktopShellState['restoredModelId'];
  restoredCollaboration: DesktopShellState['restoredCollaboration'];
  executionMode: DesktopShellState['executionMode'];
  workspace: DesktopShellState['workspace'];
  syncedWorkspaces: DesktopShellState['syncedWorkspaces'];
  workspaceEnvironment: DesktopShellState['workspaceEnvironment'];
  cloudRuntime: DesktopShellState['cloudRuntime'];
  cloudHandoffContext: string | null;
  generatedConversationTitle: DesktopShellState['generatedConversationTitle'];
  referenceResolverRef: React.MutableRefObject<
    (kind: MentionTarget['kind'], id: string) => Promise<unknown>
  >;
  personalWorkspaceRoot?: string | null;
}

/** The agent as a conversation runs it: in a worktree, with its repository's environment. */
export function runtimeAgentFor(
  agent: NativeAgent,
  executionMode: ExecutionMode,
  environment: NativeWorkspaceEnvironment | null,
): NativeAgent {
  return executionMode !== 'worktree' || environment === null
    ? agent
    : { ...agent, env: { ...agent.env, ...environment.env } };
}

/** Resolves the selected agent/model/runtime and owns its ACP connection. */
export function useDesktopAgentConnection({
  preview,
  hydrationReady,
  chatId,
  agents,
  agentId,
  restoredModelId,
  restoredCollaboration,
  executionMode,
  workspace,
  syncedWorkspaces,
  workspaceEnvironment,
  cloudRuntime,
  cloudHandoffContext,
  generatedConversationTitle,
  referenceResolverRef,
  personalWorkspaceRoot = null,
}: DesktopAgentConnectionOptions) {
  const cloudAgentSelected = executionMode === 'cloud';
  const agentRuntime: 'cloud' | 'local' = cloudAgentSelected ? 'cloud' : 'local';
  const persistedAgent = useMemo(
    () => resolvePersistedAgent(agents, agentId) ?? null,
    [agentId, agents],
  );
  const agent = persistedAgent;
  const conversationModelId = restoredModelId;
  const selectedCloudRuntime = cloudAgentSelected ? cloudRuntime : null;
  const agentWorkspace = selectedCloudRuntime?.workspaceRoot ?? workspace;
  const logicalWorkspaceId = syncedWorkspaces.find(
    (candidate) => candidate.rootPath === agentWorkspace && candidate.registered === true,
  )?.logicalWorkspaceId;
  const runtimeAgent = useMemo(
    () => (agent === null ? null : runtimeAgentFor(agent, executionMode, workspaceEnvironment)),
    [agent, executionMode, workspaceEnvironment],
  );
  // A remote conversation has no folder here until its sandbox connects: its workspace path is
  // the sandbox's, so nothing local (plugin leases, plugin MCP) may open it meanwhile.
  const awaitingCloudRuntime = cloudAgentSelected && selectedCloudRuntime === null;
  const connection = useAgentRuntime({
    conversationId: chatId,
    agent: preview || !hydrationReady || awaitingCloudRuntime ? null : runtimeAgent,
    workspaceRoot: preview || awaitingCloudRuntime ? null : agentWorkspace,
    logicalWorkspaceId,
    cloudRuntime: preview ? null : selectedCloudRuntime,
    // Cloud and local turns are both persisted by the server they go through.
    serverPersisted: cloudAgentSelected || isLocalMachine(),
    initialSessionContext: cloudHandoffContext,
    persistenceTitle: generatedConversationTitle,
    persistenceWorkspace:
      cloudAgentSelected || agentWorkspace === null
        ? null
        : {
            name: desktopWorkspaceName(agentWorkspace, personalWorkspaceRoot),
            runtime: executionMode === 'worktree' ? 'worktree' : 'local',
          },
    restoredCollaboration,
    preferredModelId: conversationModelId,
    resolveReference: (kind, id) => referenceResolverRef.current(kind, id),
  });

  return {
    agent,
    agentRuntime,
    agentWorkspace,
    cloudAgentSelected,
    connection,
    conversationModelId,
    selectedCloudRuntime,
  };
}
